<script>var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createEventDispatcher, onMount } from 'svelte';
import { writable } from 'svelte/store';
import hljs from 'highlight.js/lib/core';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/javascript';
import 'highlight.js/styles/github-dark.css';
hljs.registerLanguage('css', css);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('python', python);
export const storeHighlightJs = writable(undefined);
// Event Handler
const dispatch = createEventDispatcher();
// Props
/** Sets a language alias for Highlight.js syntax highlighting. */
export let language = 'plaintext';
/** Provide the code snippet to render. Be mindful to escape as needed! */
export let code = '';
/** Specify if line numbers should be added to the code block*/
export let lineNumbers = false;
// Local
let formatted = false;
let displayCode = code;
let copyState = false;
// Allow shorthand alias, but show full text in UI
function languageFormatter(lang) {
    if (lang === 'js')
        return 'javascript';
    if (lang === 'ts')
        return 'typescript';
    return lang;
}
// Handle Copy Text
function onCopyClick(text) {
    copyState = true;
    let temp = document.createElement('textarea');
    temp.value = text;
    document.body.appendChild(temp);
    temp.select();
    document.execCommand('copy');
    document.body.removeChild(temp);
    setTimeout(() => {
        copyState = false;
    }, 2000);
    dispatch('copy', {});
}
// Trigger syntax highlighting if highlight.js is available
$: if ($storeHighlightJs !== undefined) {
    displayCode = $storeHighlightJs.highlight(code, { language }).value.trim();
    formatted = true;
}
$: if (lineNumbers) {
    displayCode = displayCode.replace(/^/gm, () => {
        return '<span class="line"></span>\t';
    });
}
onMount(() => __awaiter(void 0, void 0, void 0, function* () {
    storeHighlightJs.set(hljs);
}));
</script>

<svelte:head>
	<link rel="preconnect" href="https://fonts.googleapis.com" />
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
	<link
		href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;300&display=swap"
		rel="stylesheet"
	/>
</svelte:head>
<!-- prettier-ignore -->
{#if language && code}
<div class="codeblock" data-testid="codeblock">
	<!-- Header -->
	<div class="codeblock-header ">
		<!-- Language -->
		<span class="codeblock-language">{languageFormatter(language)}</span>
		<!-- Copy Button -->
		<button class="codeblock-btn " on:click={() => onCopyClick(code)}>
            {#if copyState}
            <span>Copied!</span>
            {:else}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_2182_19045)">
                <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="#D0D0D2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3.33301 9.99967H2.66634C2.31272 9.99967 1.97358 9.8592 1.72353 9.60915C1.47348 9.3591 1.33301 9.01996 1.33301 8.66634V2.66634C1.33301 2.31272 1.47348 1.97358 1.72353 1.72353C1.97358 1.47348 2.31272 1.33301 2.66634 1.33301H8.66634C9.01996 1.33301 9.3591 1.47348 9.60915 1.72353C9.8592 1.97358 9.99967 2.31272 9.99967 2.66634V3.33301" stroke="#D0D0D2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                <clipPath id="clip0_2182_19045">
                <rect width="16" height="16" fill="white"/>
                </clipPath>
                </defs>
                </svg>
            {/if}

		</button>
	</div>
	<!-- Pre/Code -->
	<pre class="codeblock-pre"><code class="codeblock-code language-{language} lineNumbers">{#if formatted}{@html displayCode}{:else}{code.trim()}{/if}</code></pre>
</div>
{/if}

<style>
	.codeblock {
		border-radius: 0.3em;
		background: #282a36;
		padding: 1em;
	}
	.codeblock-header {
		font-family: 'Roboto Mono', monospace;

		display: flex;
		justify-content: space-between;
		color: #d0d0d2;
	}

	.codeblock header button {
		background: transparent;
		border: none;
		cursor: pointer;
		color: #d0d0d2;
	}
	.codeblock pre {
		padding: unset;
	}
	code[class*='language-'],
	pre[class*='language-'] {
		color: #f8f8f2;
		background: none;
		text-shadow: 0 1px rgba(0, 0, 0, 0.3);
		font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
		text-align: left;
		white-space: pre;
		word-spacing: normal;
		word-break: normal;
		word-wrap: normal;
		line-height: 1.5;
		-moz-tab-size: 4;
		-o-tab-size: 4;
		tab-size: 4;
		-webkit-hyphens: none;
		-moz-hyphens: none;
		-ms-hyphens: none;
		hyphens: none;
		border-radius: 4px;
	}
</style>
